import { Grid, TextField, Typography } from "@mui/material";
import ReadMoreReadLess from "../../app/common/ReadMoreReadLess";
import { AnswersProps } from "../../app/models/Answers";
import { useForm } from "react-hook-form";
import { useAppSelector } from "../../app/store/configureStore";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const schema = yup.object().shape({
  social: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
  emotional: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
  positive: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
});

const SocialDevelopmentList: React.FC<AnswersProps> = ({
  answersData,
  onAnswerChange,
}) => {
  const defaultValues = {
    social: answersData.find((answer) => answer.systemCodeId === 171)?.answer,
    emotional: answersData.find((answer) => answer.systemCodeId === 172)
      ?.answer,
    positive: answersData.find((answer) => answer.systemCodeId === 173)?.answer,
  };

  const {
    register,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  const { user } = useAppSelector((state) => state.account);

  const renderSocialDevelopmentCompetence = () => {
    return (
      <>
        {user?.credentialTypeId === 4 ? (
          <ul>
            <li>
              What steps have you implemented to ensure the home environment
              provides opportunities for children to experience cooperation?
            </li>
            <li>
              What resources and materials have you used to help parents promote
              feelings of empathy and respect for others?
            </li>
            <li>
              What strategies have you employed to help parents understand the
              typical social behaviors for children?
            </li>
            <li>
              How do you partner with families to ensure diverse activities,
              materials, curriculum and/or events reflect an understanding of
              others' cultures and the value of a non- bias and anti-bias
              environment?
            </li>
          </ul>
        ) : (
          <ul>
            <li>
              What strategies do you use to help children express their
              feelings?
            </li>
            <li>
              What daily activities do you use to help children with positive
              peer interactions?
            </li>
            <li>
              How are program activities adapted to meet the individual needs of
              all children?
            </li>
            <li>
              How do you create opportunities to allow recognition and
              appreciation of racial, ethnic, and ability differences and
              similarities?
            </li>
          </ul>
        )}
      </>
    );
  };

  const renderEmotionalDevelopmentCompetence = () => {
    return (
      <>
        {user?.credentialTypeId === 4 ? (
          <ul>
            <li>
              What techniques do you use to help children develop a sense of
              security within the home?
            </li>
            <li>
              What strategies do you use to help parents provide consistency in
              daily activities and routines?
            </li>
            <li>
              How do you support parents with implementing self-help skills to
              promote their child's independence?
            </li>
          </ul>
        ) : (
          <ul>
            <li>
              What daily practices do you use to help each child develop a sense
              of security within the classroom?
            </li>
            <li>
              What strategies do you use to ensure consistency of daily
              activities and routines?
            </li>
            <li>
              What activities and practices do you use to support children
              through daily transitions?
            </li>
            <li>
              What self-help skills do you use to promote each child's
              developing independence?
            </li>
          </ul>
        )}
      </>
    );
  };

  const renderPostiiveGuidanceCompetence = () => {
    return (
      <>
        {user?.credentialTypeId === 4 ? (
          <ul>
            <li>
              How do you assist parents in learning and using positive guidance
              techniques?
            </li>
            <li>
              How do you support parents to create daily activities to gently
              guide children and promote self-regulation?
            </li>
            <li>
              What steps do you take to ensure spaces and materials within the
              home support children's developmental needs and prevent disruptive
              behaviors?
            </li>
            <li>
              What resources do you provide to help parents create a positive
              environment?
            </li>
          </ul>
        ) : (
          <ul>
            <li>
              What appropriate positive guidance techniques do you use to
              support each child's temperament and developmental level?
            </li>
            <li>
              What materials and resources do you use to create a positive
              environment?
            </li>
            <li>
              What daily activities do you use to encourage positive peer
              interactions?
            </li>
            <li>
              How do you involve children in creating guidelines for acceptable
              and appropriate behaviors?
            </li>
          </ul>
        )}
      </>
    );
  };

  return (
    <>
      <Grid container pb={3}>
        <Grid item xs={12} px={3} pt={3}>
          <Typography variant="body1" fontWeight="bold" paragraph>
            Social Development
          </Typography>
          <Typography variant="body1" paragraph>
            How do your teaching practices support social development?
          </Typography>
          <ReadMoreReadLess>
            <>{renderSocialDevelopmentCompetence()}</>
          </ReadMoreReadLess>
        </Grid>
        <Grid item xs={12} px={3} pt={3}>
          <TextField
            label={"In my teaching practices, I support social development by"}
            multiline
            rows={2}
            fullWidth
            {...register("social")}
            name="social"
            sx={{ bgcolor: "white" }}
            value={
              answersData.find((answer) => answer.systemCodeId === 171)
                ?.answer || ""
            }
            onChange={(e) => {
              const newValue = e.target.value;
              const systemCodeId = 171;

              newValue.length <= 500 && onAnswerChange(systemCodeId, newValue);
            }}
            error={!!errors.social?.message}
            helperText={errors.social?.message}
          />
          <Typography fontSize={13}>
            {defaultValues.social?.length}/500
          </Typography>
        </Grid>
        <Grid item xs={12} px={3} pt={3}>
          <Typography variant="body1" fontWeight="bold" paragraph>
            Emotional Development
          </Typography>
          <Typography variant="body1" paragraph>
            How do your teaching practices support emotional development?
          </Typography>
          <ReadMoreReadLess>
            <>{renderEmotionalDevelopmentCompetence()}</>
          </ReadMoreReadLess>
        </Grid>
        <Grid item xs={12} px={3} pt={3}>
          <TextField
            label={
              "In my teaching practices, I support emotional development by"
            }
            multiline
            rows={2}
            fullWidth
            {...register("emotional")}
            name="emotional"
            sx={{ bgcolor: "white" }}
            value={
              answersData.find((answer) => answer.systemCodeId === 172)
                ?.answer || ""
            }
            onChange={(e) => {
              const newValue = e.target.value;
              const systemCodeId = 172;
              newValue.length <= 500 && onAnswerChange(systemCodeId, newValue);
            }}
            error={!!errors.emotional?.message}
            helperText={errors.emotional?.message}
          />
          <Typography fontSize={13}>
            {defaultValues.emotional?.length}/500
          </Typography>
        </Grid>
        <Grid item xs={12} px={3} pt={3}>
          <Typography variant="body1" fontWeight="bold" paragraph>
            Positive Guidance
          </Typography>
          <Typography variant="body1" paragraph>
            {user?.credentialTypeId === 4
              ? "How do your teaching practices help families provide positive guidance for their children?"
              : "How do you provide positive guidance in your teaching practices?"}
          </Typography>
          <ReadMoreReadLess>
            <>{renderPostiiveGuidanceCompetence()}</>
          </ReadMoreReadLess>
        </Grid>
        <Grid item xs={12} px={3} pt={3}>
          <TextField
            label={"In my teaching practices, I provide positive guidance by"}
            multiline
            rows={2}
            fullWidth
            {...register("positive")}
            name="positive"
            sx={{ bgcolor: "white" }}
            value={
              answersData.find((answer) => answer.systemCodeId === 173)
                ?.answer || ""
            }
            onChange={(e) => {
              const newValue = e.target.value;
              const systemCodeId = 173;

              newValue.length <= 500 && onAnswerChange(systemCodeId, newValue);
            }}
            error={!!errors.positive?.message}
            helperText={errors.positive?.message}
          />
          <Typography fontSize={13}>
            {defaultValues.positive?.length}/500
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};

export default SocialDevelopmentList;
